import { useRef, useCallback, useEffect } from 'react';

/**
 * useDrawerHeightTransition
 *
 * Smoothly animates a container's height when its content changes size
 * (e.g., switching between form pages of different heights in a Vaul drawer).
 *
 * Usage:
 *   const { containerRef, prepareTransition } = useDrawerHeightTransition(isDrawerOpenOrDynamicEnabled);
 *
 *   // Attach containerRef to the page-container div
 *   <div ref={containerRef} className="page-container drawer-height-animate">
 *
 *   // Call prepareTransition() BEFORE changing the page state
 *   const handleSetPage = (page, direction) => {
 *     prepareTransition();
 *     setCurrentPage(page);
 *   };
 *
 * How it works:
 *   1. prepareTransition() captures the current container height and locks it
 *   2. React re-renders with new page → content changes size
 *   3. On next frame, we measure the new natural height and animate to it
 *   4. On transitionend, we release the explicit height back to auto
 *
 * Pass true when the drawer is open, or when using dynamic (non-overlay) mode
 * so detail ↔ form height changes animate on the same page-container ref.
 * Overlay mode should pass false so height locking never runs.
 *
 * While the height is actually being animated the container also carries
 * `HEIGHT_ANIMATING_CLASS`, and that is the whole of CON-162. In dynamic mode
 * the `.frosted-background` card is a descendant of this element, and its
 * `backdrop-filter` re-rasterises the blur for every frame the ancestor's box
 * interpolates. `forms.css` drops the filter for exactly the span this class is
 * on, so the blur is computed once at the start and once at the end instead of
 * once per frame. The hook already owns the only two moments that matter — the
 * write that starts the movement, and the single release path — so the class is
 * added and removed there and nowhere else: no new timer, no new listener, and
 * no change to the height lock itself.
 */
/**
 * On the `.page-container` for exactly as long as the hook is driving its
 * height. Read by `forms.css` to suppress a descendant frosted card's blur
 * (CON-162). Exported so the tests name the same string the CSS does.
 */
export const HEIGHT_ANIMATING_CLASS = 'cs-height-animating';

/**
 * How long the safety timer must outlast the transition it guards, in ms.
 * Covers the frame the transition starts on plus a slow phone's jitter; small
 * enough that a genuinely stuck transition still releases promptly.
 */
export const SAFETY_TIMER_MARGIN_MS = 120;

/**
 * Floor for the safety timer. Under `prefers-reduced-motion: reduce` the motion
 * tokens collapse to 0.01ms, and a zero-duration transition fires NO
 * `transitionend` at all — so the timer is the ONLY release path there and must
 * not be instant (the class still has to survive the frame the box moves in) or
 * long (a reduced-motion donor should not wait out a timer for a transition
 * that already finished).
 */
export const SAFETY_TIMER_FLOOR_MS = 150;

/**
 * The element's own resolved transition duration, in ms.
 *
 * CON-173: this used to be the literal 400, which was SHORTER than the
 * transition it guarded in the branch the frosted card actually renders in.
 * `.page-container[data-mode="dynamic"]` animates height over
 * `--cs-motion-slow` (500ms) while the drawer branch uses `--cs-motion-base`
 * (300ms) — so every non-drawer step change had its glide cut at 400ms and
 * snapped the rest in one frame, and since the frosted-card fix the blur also
 * came back 100ms before the box stopped moving.
 *
 * Reading the resolved value rather than restating a number is what stops the
 * two drifting again: it is correct in BOTH branches, and it follows the tokens
 * if their values ever change — including collapsing to ~0 under reduced
 * motion, which a hard-coded number cannot do.
 */
export function resolveSafetyTimerMs(el, view) {
  const DEFAULT_MS = 500 + SAFETY_TIMER_MARGIN_MS;
  try {
    // Only fall back to the global when NO view was supplied (a detached node).
    // A view that was supplied but cannot report style is an unreadable style,
    // not an invitation to measure against some other document — so it takes
    // the conservative default rather than the floor.
    const owner = view ?? globalThis;
    const readStyle = owner?.getComputedStyle;
    if (typeof readStyle !== 'function') return DEFAULT_MS;
    const style = readStyle.call(owner, el);
    if (!style) return DEFAULT_MS;

    // Both are comma-separated, one entry per transitioned property. The
    // longest is what the element finishes on, so that is what has to be
    // outlasted. A `transitionDelay` is included because it postpones the end
    // just as surely as the duration extends it.
    const toMs = (list) =>
      String(list || '')
        .split(',')
        .map((part) => {
          const value = part.trim();
          if (!value) return 0;
          const n = Number.parseFloat(value);
          if (!Number.isFinite(n)) return 0;
          return value.endsWith('ms') ? n : n * 1000;
        });

    const durations = toMs(style.transitionDuration);
    const delays = toMs(style.transitionDelay);
    if (durations.length === 0) return DEFAULT_MS;

    const longest = durations.reduce(
      (max, d, i) => Math.max(max, d + (delays[i] ?? delays[0] ?? 0)),
      0,
    );
    // A stylesheet that has not applied yet reads as 0s on every property, and
    // that is indistinguishable from reduced motion here. The floor covers both:
    // it releases quickly enough for reduced motion and still leaves a real
    // transition its own `transitionend` as the primary path.
    return Math.max(SAFETY_TIMER_FLOOR_MS, Math.round(longest) + SAFETY_TIMER_MARGIN_MS);
  } catch {
    return DEFAULT_MS;
  }
}

export function useDrawerHeightTransition(enabled) {
  const containerRef = useRef(null);
  const isAnimatingRef = useRef(false);
  // The element's own inline min-height, so the lock below can be undone
  // exactly rather than blanked.
  const priorMinHeightRef = useRef('');
  // The element the blur-suppression class was put on. `containerRef.current` is
  // already null by the time an unmount cleanup runs, so the cleanup needs its
  // own handle or it has nothing to take the class off.
  const animatingElRef = useRef(null);

  // Unmount mid-transition: React has detached the node, but the hook's own
  // release path may never run (the safety timer is cleared with the component).
  // Take the class off so a node React reuses cannot come back unblurred.
  useEffect(
    () => () => {
      if (animatingElRef.current) {
        animatingElRef.current.classList.remove(HEIGHT_ANIMATING_CLASS);
        animatingElRef.current = null;
      }
    },
    [],
  );

  const prepareTransition = useCallback(() => {
    if (!enabled || !containerRef.current || isAnimatingRef.current) return;

    const el = containerRef.current;
    const currentHeight = el.offsetHeight;

    // Lock height to current value.
    //
    // AND min-height, which is what makes the lock actually hold (CON-21). The
    // container carries `min-height: fit-content` — from `.page-container` and,
    // on the widget, from an inline style as well — so the used height is
    // max(height, content) and a `height` shorter than the incoming content is
    // simply ignored. Measured on the shipped bundle: locking `height` 60px
    // below the content left the rendered height unchanged at 584px; locking
    // both rendered 524px. Without this the hook ran its whole sequence and the
    // element snapped anyway, in every direction where the new content is
    // taller than the old — which is most of them.
    priorMinHeightRef.current = el.style.minHeight;
    el.style.height = `${currentHeight}px`;
    el.style.minHeight = `${currentHeight}px`;
    isAnimatingRef.current = true;

    // After React re-renders (next frame), measure new content and animate
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Unmounted between the lock and this frame: release the flag or the
        // hook stays permanently in-progress and never animates again.
        if (!containerRef.current) {
          isAnimatingRef.current = false;
          return;
        }

        // Temporarily remove both locks to measure natural height
        el.style.height = 'auto';
        el.style.minHeight = priorMinHeightRef.current;
        const newHeight = el.offsetHeight;

        // One release path, and it tears down BOTH of its own hooks. The safety
        // timer used to outlive an early `transitionend` — so a finished run's
        // timer fired during the NEXT run, saw the flag set again, and released
        // that run's lock mid-glide. The listener leaked for the same reason.
        //
        // CON-173: the timer's LENGTH is derived from the element's own
        // resolved transition, not restated. The old comment here described the
        // drawer branch only (`--cs-motion-base`, 300ms, guarded by 400ms) and
        // was silently wrong for dynamic mode (`--cs-motion-slow`, 500ms),
        // which is the branch the frosted card renders in — the timer fired
        // 100ms EARLY there, on every step change.
        let safetyTimer = null;
        const release = () => {
          el.style.height = '';
          el.style.minHeight = priorMinHeightRef.current;
          isAnimatingRef.current = false;
          // The one release path, so the one place the blur comes back. Both
          // ways in reach it: the filtered `transitionend` and the safety timer.
          el.classList.remove(HEIGHT_ANIMATING_CLASS);
          animatingElRef.current = null;
          if (safetyTimer !== null) {
            clearTimeout(safetyTimer);
            safetyTimer = null;
          }
          el.removeEventListener('transitionend', onEnd);
        };

        // If heights are the same, no animation needed
        if (Math.abs(newHeight - currentHeight) < 2) {
          release();
          return;
        }

        // Snap back to old height, then animate to new
        el.style.height = `${currentHeight}px`;
        el.style.minHeight = `${currentHeight}px`;
        // Force reflow
        el.offsetHeight; // eslint-disable-line no-unused-expressions

        // CON-162: the box is about to actually move, so stop blurring what is
        // behind it. Deliberately HERE and not at the lock two frames earlier,
        // and the reason is the failure mode rather than the frame count: the
        // lock runs synchronously, but the safety timer that takes the class
        // back off is armed further down THIS callback. A class added at the
        // lock therefore spends the gap between them covered by nothing at all,
        // and anything that stops the document's rendering steps in that gap —
        // a backgrounded tab, a hidden iframe, offscreen-iframe throttling —
        // strands the card un-frosted until rendering resumes. Added here, the
        // class and its ceiling are created together and no such window exists.
        //
        // Nothing is lost by waiting: the lock writes the height the element
        // already has, and the measure/snap-back above resolves inside this same
        // callback behind a forced reflow, so the first frame the browser paints
        // with a CHANGED box is the one after this write.
        //
        // It is also below the `< 2px` early release on purpose — a run that
        // never animates must not blink the frost off and on for a box that
        // does not move.
        el.classList.add(HEIGHT_ANIMATING_CLASS);
        animatingElRef.current = el;

        el.style.height = `${newHeight}px`;
        el.style.minHeight = `${newHeight}px`;

        // Only THIS element's `height` ends the lock. `transitionend` bubbles, and
        // the container is full of transitioning children, so an unfiltered
        // handler releases on whichever child finishes first — measured cutting
        // the glide short at 581 of 592px (CON-21). `min-height` is filtered out
        // for the same reason: it is transitioned alongside `height` here, so
        // whichever of the two settles first would otherwise end both.
        function onEnd(event) {
          if (event.target !== el || event.propertyName !== 'height') return;
          release();
        }
        el.addEventListener('transitionend', onEnd);

        // Safety net, not the mechanism: `transitionend` above is the normal
        // release. This only fires when that event never arrives — a
        // zero-duration (reduced-motion) transition, an interrupted one, or a
        // tab that stopped rendering.
        const safetyMs = resolveSafetyTimerMs(el, el.ownerDocument?.defaultView);
        safetyTimer = setTimeout(() => {
          safetyTimer = null;
          if (isAnimatingRef.current) {
            release();
          }
        }, safetyMs);
      });
    });
  }, [enabled]);

  return { containerRef, prepareTransition };
}

export default useDrawerHeightTransition;
